import { Fragment } from 'react'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  SortableTableHead,
} from '@/components/ui/table'
import type { RegistrationPeople, RegistrationPerson } from '@/lib/registration-people'

export function RegistrationPeopleTable({
  people,
  sort,
  onSort,
  engagement = false,
  started = false,
}: {
  people: RegistrationPeople
  engagement?: boolean
  started?: boolean
  sort: string
  onSort: (sort: string) => void
}) {
  const [key, direction] = sort.split(':')
  const columns = [
    ['person', 'Person'],
    ...(engagement ? [['ticket', 'Ticket']] : []),
    ['ordered', 'Ordered'],
    ...(engagement && started ? [['checked_now', 'Checked in']] : []),
    ...(people.has_previous
      ? [['last', 'Last year'], ...(!engagement ? [['checked', 'Checked in last year']] : [])]
      : []),
  ]
  const booked = people.rows.filter((row) => row.booked_this_year)
  const notBooked = people.rows.filter((row) => !row.booked_this_year)
  return (
    <Table aria-label="Organization people">
      <TableHeader>
        <TableRow>
          {columns.map(([column, label]) => (
            <SortableTableHead
              key={column}
              className="whitespace-nowrap"
              active={key === column}
              direction={direction === 'asc' ? 'ascending' : 'descending'}
              onToggle={() =>
                onSort(`${column}:${key === column && direction === 'asc' ? 'desc' : 'asc'}`)
              }
            >
              {label}
            </SortableTableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {booked.map((row, index) => (
          <PersonRow
            key={row.contact_id ?? `${row.kind}-${index}`}
            row={row}
            hasPrevious={people.has_previous}
            engagement={engagement}
            started={started}
          />
        ))}
        {notBooked.length > 0 && (
          <Fragment>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableCell
                colSpan={columns.length}
                className="h-9 py-0 text-caption font-medium text-muted-foreground"
              >
                Not booked this year · {notBooked.length}
              </TableCell>
            </TableRow>
            {notBooked.map((row, index) => (
              <PersonRow
                key={row.contact_id ?? `${row.kind}-${index}`}
                row={row}
                hasPrevious={people.has_previous}
                engagement={engagement}
                started={started}
              />
            ))}
          </Fragment>
        )}
      </TableBody>
    </Table>
  )
}

function PersonRow({
  row,
  hasPrevious,
  engagement,
  started,
}: {
  row: RegistrationPerson
  hasPrevious: boolean
  engagement: boolean
  started: boolean
}) {
  return (
    <TableRow className="h-9">
      <TableCell className="whitespace-nowrap py-0">
        {row.kind === 'named' && row.contact_id ? (
          <Button variant="link" size="inline" asChild className="font-normal">
            <a href={`/contacts/persons/${encodeURIComponent(row.contact_id)}`}>{row.name}</a>
          </Button>
        ) : (
          <span className="text-muted-foreground">{row.name}</span>
        )}
      </TableCell>
      {engagement && (
        <TableCell className="whitespace-nowrap py-0">{row.ticket_name ?? '—'}</TableCell>
      )}
      <TableCell className="whitespace-nowrap py-0">{row.ordered_label}</TableCell>
      {engagement && started && (
        <TableCell className="whitespace-nowrap py-0">
          {row.checked_in ? (
            <Check role="img" aria-label="Checked in" className="size-4 text-success" />
          ) : (
            '—'
          )}
        </TableCell>
      )}
      {hasPrevious && (
        <>
          <TableCell className="whitespace-nowrap py-0">
            {row.held_last_year ? 'Held a place' : '—'}
          </TableCell>
          {!engagement && (
            <TableCell className="whitespace-nowrap py-0 text-center">
              {row.checked_last ? (
                <Check
                  role="img"
                  aria-label="Checked in"
                  className="mx-auto size-4"
                  strokeWidth={1.5}
                />
              ) : (
                '—'
              )}
            </TableCell>
          )}
        </>
      )}
    </TableRow>
  )
}
